import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/Login.css";
import cardexLogo from "../assets/Cardex Login Final Logo.png";
import { register as apiRegister } from "../api/auth";
import { setToken, setRefreshToken, setUser } from "../auth/auth";

export default function Signup() {
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const navigate = useNavigate();

  const validatePassword = (pwd: string) => {
    return {
      minLength: pwd.length >= 8,
      hasUppercase: /[A-Z]/.test(pwd),
      hasLowercase: /[a-z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd),
    };
  };

  const isPasswordValid = () => {
    const checks = validatePassword(password);
    return Object.values(checks).every(Boolean);
  };

  const handleSignup = async (e: any) => {
    e.preventDefault();
    setError("");

    if (!termsAccepted) {
      setError("Please accept the Terms and Privacy Policy to continue.");
      return;
    }

    if (!ageConfirmed) {
      setError("You must confirm that you are 18 years of age or older.");
      return;
    }

    if (!isPasswordValid()) {
      setError("Password does not meet all requirements");
      return;
    }

    setLoading(true);

    try {
      const data = await apiRegister({
        name,
        username,
        email,
        password,
        location: location.trim() || null,
        termsAcceptedAt: new Date().toISOString(),
        privacyAcceptedAt: new Date().toISOString(),
        termsVersion: "2026-02-03",
        privacyVersion: "2026-02-03",
        ageConfirmed: true
      });
      if (!data?.token) {
        throw new Error("Missing auth token");
      }
      setToken(data.token);
      if (data.refreshToken) setRefreshToken(data.refreshToken);
      setUser(
        data.user || {
          name: name || "Cardex User",
          username,
          email,
          location,
          source: "email",
        }
      );

      proceedToApp();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "";
      if (message && message.toLowerCase().includes("exists")) {
        setError("An account with that email already exists");
      } else {
        setError("Could not create account");
      }
    } finally {
      setLoading(false);
    }
  };

  const proceedToApp = () => {
    // Check for return URL from guest gate
    const returnUrl = localStorage.getItem("returnUrl");
    if (returnUrl) {
      localStorage.removeItem("returnUrl");
      navigate(returnUrl);
    } else {
      navigate("/onboarding/plan");
    }
  };

  return (
    <main className="screen signup-screen auth-screen">
      <div className="bg-glow"></div>
      <div className="login-grid-lines" aria-hidden="true"></div>
      <div className="login-noise" aria-hidden="true"></div>

      <div className="login-scene auth-scene">
        <section className="hero login-hero auth-hero">
          <img src={cardexLogo} alt="Cardex" className="logo" />
          <h1 className="title auth-title">Create your CARDEX account</h1>
          <p className="subtitle auth-subtitle">
            Post bounties, hunt for cards, and connect with collectors near you.
          </p>
        </section>

        <section className="card login-panel signup-panel signup-card" aria-label="Signup form">
          <div className="login-panel-glow" aria-hidden="true"></div>

          <form className="form" onSubmit={handleSignup}>
            {error && <div className="error-msg">{error}</div>}

            <div className="signup-grid">
              <div className="field-block">
                <label className="label" htmlFor="name">Full Name</label>
                <div className="input-wrap">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e: any) => setName(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="field-block">
                <label className="label" htmlFor="username">Username</label>
                <div className="input-wrap">
                  <input
                    id="username"
                    name="username"
                    type="text"
                    placeholder="johndoe"
                    value={username}
                    pattern="^[A-Za-z0-9_]+$"
                    onChange={(e: any) => setUsername(e.target.value.replace(/\s/g, ""))}
                    required
                  />
                </div>
                <small className="helper-text">
                  Letters, numbers, and underscore only.
                </small>
              </div>

              <div className="field-block">
                <label className="label" htmlFor="email">Email Address</label>
                <div className="input-wrap">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    value={email}
                    onChange={(e: any) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="field-block field-block-full">
                <label className="label" htmlFor="password">Password</label>
                <div className="input-wrap">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e: any) => setPassword(e.target.value)}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    required
                  />
                </div>
              </div>

              {passwordFocused && (
                <div className="password-requirements field-block-full">
                  <div className="req-title">Password must contain:</div>
                  <div className={`req-item ${validatePassword(password).minLength ? "valid" : ""}`}>
                    {validatePassword(password).minLength ? "✓" : "○"} At least 8 characters
                  </div>
                  <div className={`req-item ${validatePassword(password).hasUppercase ? "valid" : ""}`}>
                    {validatePassword(password).hasUppercase ? "✓" : "○"} One uppercase letter (A-Z)
                  </div>
                  <div className={`req-item ${validatePassword(password).hasLowercase ? "valid" : ""}`}>
                    {validatePassword(password).hasLowercase ? "✓" : "○"} One lowercase letter (a-z)
                  </div>
                  <div className={`req-item ${validatePassword(password).hasNumber ? "valid" : ""}`}>
                    {validatePassword(password).hasNumber ? "✓" : "○"} One number (0-9)
                  </div>
                  <div className={`req-item ${validatePassword(password).hasSpecial ? "valid" : ""}`}>
                    {validatePassword(password).hasSpecial ? "✓" : "○"} One special character (!@#$%^&*)
                  </div>
                </div>
              )}

              <div className="field-block field-block-full">
                <label className="label" htmlFor="location">Location (Optional - for local trades)</label>
                <div className="input-wrap">
                  <input
                    id="location"
                    name="location"
                    type="text"
                    placeholder="Los Angeles, CA"
                    value={location}
                    onChange={(e: any) => setLocation(e.target.value)}
                  />
                </div>
                <p className="location-hint">You can set this later if you want to trade locally.</p>
              </div>
            </div>

            <label className="terms-checkbox-label">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e: any) => setTermsAccepted(e.target.checked)}
                required
              />
              <span>
                I agree to the Cardex{" "}
                <Link to="/terms" className="legal-link" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </Link>
                {" "}and{" "}
                <Link to="/privacy" className="legal-link" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </Link>.
              </span>
            </label>
            <p className="checkbox-helper">You must accept to create an account.</p>

            <label className="terms-checkbox-label">
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e: any) => setAgeConfirmed(e.target.checked)}
                required
              />
              <span>
                I confirm that I am <strong>18 years of age or older</strong>.
              </span>
            </label>
            <p className="checkbox-helper">Cardex is an adults-only platform.</p>

            <button className="btn-primary login-submit login-submit--prominent" type="submit" disabled={loading || !termsAccepted || !ageConfirmed}>
              <span>{loading ? "Creating..." : "Create Account"}</span>
            </button>
          </form>

          <footer className="footer login-footer">
            <p>
              Already have an account?{" "}
              <Link className="link strong" to="/login">Log in</Link>
            </p>
          </footer>
        </section>
      </div>

    </main>
  );
}
